import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchChatMessagesApi, revertChatMessageApi, sendChatMessageApi } from '../../api/chat.api';
import { deleteProjectChat } from './chatsSlice';
import { fetchProjectById } from './projectsSlice';
import { hasPreviewFiles } from '../../utils/previewFiles';
import { runProjectSandbox } from './previewSlice';
import { fetchWalletBalance } from './walletSlice';

const ensureChatMessages = (state, chatId) => {
  if (!state.byChatId[chatId]) {
    state.byChatId[chatId] = [];
  }
};

const findStreamingMessage = (state, chatId, requestId) =>
  state.byChatId[chatId]?.find((item) => item.requestId === requestId && item.role === 'assistant');

const findAgentStreamingAssistant = (state, chatId) =>
  [...(state.byChatId[chatId] || [])]
    .reverse()
    .find(
      (item) =>
        item.role === 'assistant' &&
        item.isStreaming &&
        String(item.requestId || '').startsWith('agent-')
    );

const LLM_FALLBACK_ERROR_MESSAGE = 'مشکلی رخ داده است مجددا تلاش کنید';

const GENERATION_PHASE_ORDER = [
  'connecting',
  'connected',
  'developing',
  'developed',
  'launching',
  'running',
  'ready'
];

const canAdvanceGenerationPhase = (current, next) => {
  if (!next) {
    return false;
  }
  if (!current) {
    return true;
  }
  if (current === 'ready' && next !== 'ready') {
    return false;
  }
  return GENERATION_PHASE_ORDER.indexOf(next) >= GENERATION_PHASE_ORDER.indexOf(current);
};


const finalizeStreamingMessage = (state, { chatId, requestId, reply }) => {
  const streamingMessage = findStreamingMessage(state, chatId, requestId);

  if (!streamingMessage) {
    return;
  }

  if (reply) {
    streamingMessage.message = reply;
    streamingMessage.content = reply;
  }

  streamingMessage.isStreaming = false;
};

const getChatIdFromArg = (arg) => (typeof arg === 'object' ? arg.chatId : arg);

const roleSortRank = (role) => {
  if (role === 'user') return 0;
  if (role === 'assistant') return 1;
  return 2;
};

const compareChatItems = (left, right) => {
  const leftTime = new Date(left.createdAt || 0).getTime();
  const rightTime = new Date(right.createdAt || 0).getTime();
  if (leftTime !== rightTime) {
    return leftTime - rightTime;
  }

  const roleDiff = roleSortRank(left.role) - roleSortRank(right.role);
  if (roleDiff !== 0) {
    return roleDiff;
  }

  return String(left.id || '').localeCompare(String(right.id || ''));
};

/** Keep the live streaming agent reply at the end of the thread. */
const sortChatThreadItems = (items) => {
  const rest = [];
  let streamingAssistant = null;

  items.forEach((item) => {
    if (
      item.role === 'assistant' &&
      item.isStreaming &&
      String(item.requestId || '').startsWith('agent-')
    ) {
      streamingAssistant = item;
      return;
    }

    rest.push(item);
  });

  rest.sort(compareChatItems);

  if (!streamingAssistant) {
    return rest;
  }

  return [...rest, streamingAssistant];
};

const lastMessageHasPreviewFiles = (messages) => {
  const lastMessage = messages[messages.length - 1];

  if (!lastMessage) {
    return false;
  }

  return hasPreviewFiles(lastMessage.files);
};

const runSandboxAfterLlmResponse = async ({ chatId, projectId, signal }, { dispatch }) => {
  if (signal?.aborted) {
    return;
  }

  const { messages } = await dispatch(fetchChatMessages({ chatId })).unwrap();

  if (signal?.aborted) {
    return;
  }

  if (!projectId || !lastMessageHasPreviewFiles(messages)) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    if (signal?.aborted) {
      return;
    }
    dispatch(setGenerationPhase({ chatId, phase: 'ready' }));
    return;
  }

  dispatch(setGenerationPhase({ chatId, phase: 'launching' }));
  const sandboxPromise = dispatch(runProjectSandbox({ projectId }))
    .unwrap()
    .catch((error) => {
      console.warn(error?.message || 'Sandbox preview failed.');
    });

  await new Promise((resolve) => setTimeout(resolve, 650));
  if (signal?.aborted) {
    return;
  }
  dispatch(setGenerationPhase({ chatId, phase: 'running' }));
  await sandboxPromise;
  if (signal?.aborted) {
    return;
  }
  await new Promise((resolve) => setTimeout(resolve, 400));
  if (signal?.aborted) {
    return;
  }
  dispatch(setGenerationPhase({ chatId, phase: 'ready' }));
};

export const fetchChatMessages = createAsyncThunk('chat/fetchMessages', async (arg) => {
  const chatId = getChatIdFromArg(arg);
  const messages = await fetchChatMessagesApi(chatId);
  return { chatId, messages };
});

const refreshAfterRevert = async ({ chatId, projectId }, { dispatch }) => {
  try {
    await dispatch(fetchChatMessages({ chatId })).unwrap();
  } catch (error) {
    console.warn(error?.message || 'Chat messages refresh failed.');
  }

  if (!projectId) {
    return;
  }

  try {
    await dispatch(fetchProjectById(projectId)).unwrap();
  } catch (error) {
    console.warn(error?.message || 'Project refresh failed.');
  }

  try {
    await dispatch(runProjectSandbox({ projectId })).unwrap();
  } catch (error) {
    console.warn(error?.message || 'Sandbox preview failed.');
  }
};

export const revertChatMessage = createAsyncThunk(
  'chat/revertMessage',
  async ({ messageId, chatId, projectId }, { dispatch }) => {
    await revertChatMessageApi(messageId);

    void refreshAfterRevert({ chatId, projectId }, { dispatch });

    return { chatId, messageId };
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (
    { chatId, projectId, projectName, message, language = 'en', model, provider, imageUrls = [], quotedText },
    { dispatch, requestId, signal, rejectWithValue }
  ) => {
    try {
      const apiPrompt = quotedText
        ? `کاربر به این نقل‌قول اشاره کرده:\n"${quotedText}"\n\n${message}`
        : message;
      const response = await sendChatMessageApi({
        chatId,
        projectId,
        projectName,
        message: apiPrompt,
        model,
        provider,
        imageUrls,
        signal,
        onStart: () => {
          dispatch(setGenerationPhase({ chatId, phase: 'connected' }));
        },
        onDelta: (_chunk, fullText) => {
          dispatch(streamDeltaReceived({ chatId, requestId, content: fullText }));
          dispatch(setGenerationPhase({ chatId, phase: 'developing' }));
        },
        onResult: (result) => {
          dispatch(streamResultReceived({ chatId, requestId, result }));
        }
      });

      dispatch(
        finalizeStreamingMessageAction({
          chatId,
          requestId,
          reply: response.reply
        })
      );
      dispatch(setGenerationPhase({ chatId, phase: 'developed' }));

      void dispatch(fetchWalletBalance({ silent: true }));

      await runSandboxAfterLlmResponse({ chatId, projectId, signal }, { dispatch });

      return { chatId, projectId, message, response };
    } catch (error) {
      if (error?.statusCode === 402) {
        return rejectWithValue({
          message: error.message,
          statusCode: 402
        });
      }

      throw error;
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    byChatId: {},
    latestResultByChatId: {},
    generationByChatId: {},
    loading: false,
    historyLoading: false,
    error: null,
    historyError: null
  },
  reducers: {
    hydrateProjectChat: (state, action) => {
      const chatId = action.payload;

      if (!chatId) {
        return;
      }

      ensureChatMessages(state, chatId);
      state.historyError = null;
    },
    setMessageFeedback: (state, action) => {
      const { chatId, messageId, feedback } = action.payload;
      const message = state.byChatId[chatId]?.find((item) => item.id === messageId);

      if (message) {
        message.feedback = feedback || null;
      }
    },
    setGenerationPhase: (state, action) => {
      const { chatId, phase } = action.payload;
      if (!chatId || !phase) {
        return;
      }
      const current = state.generationByChatId[chatId]?.phase;
      if (!canAdvanceGenerationPhase(current, phase)) {
        return;
      }
      const previous = state.generationByChatId[chatId] || {};
      state.generationByChatId[chatId] = {
        phase,
        sandbox: Boolean(previous.sandbox || phase === 'launching' || phase === 'running')
      };
    },
    clearGenerationPhase: (state, action) => {
      const chatId = action.payload;
      if (!chatId) {
        return;
      }
      delete state.generationByChatId[chatId];
    },
    streamDeltaReceived: (state, action) => {
      const { chatId, requestId, content } = action.payload;
      const streamingMessage = requestId
        ? findStreamingMessage(state, chatId, requestId)
        : findAgentStreamingAssistant(state, chatId);

      if (streamingMessage) {
        streamingMessage.content = content;
        streamingMessage.message = content;
      }
    },
    streamResultReceived: (state, action) => {
      const { chatId, requestId, result } = action.payload;
      state.latestResultByChatId[chatId] = result;
      const finalMessage = result?.message || result?.Message || result?.data?.message || result?.Data?.Message;
      const streamingMessage = findStreamingMessage(state, chatId, requestId);

      if (streamingMessage && finalMessage) {
        streamingMessage.message = finalMessage;
        streamingMessage.content = finalMessage;
      }
    },
    finalizeStreamingMessageAction: (state, action) => {
      finalizeStreamingMessage(state, action.payload);
    },
    appendAgentTurnOptimistic: (state, action) => {
      const { chatId, message, imageUrls = [], quotedText = null } = action.payload;
      if (!chatId) {
        return;
      }
      ensureChatMessages(state, chatId);
      // Drop leftover activity rows from a prior turn.
      state.byChatId[chatId] = state.byChatId[chatId].filter((item) => item.role !== 'agent-activity');
      const stamp = Date.now();
      state.byChatId[chatId].push({
        id: `user-agent-${stamp}`,
        role: 'user',
        content: message || '',
        imageUrls: Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [],
        quotedText: quotedText || null,
        createdAt: new Date().toISOString()
      });
      state.byChatId[chatId].push({
        id: `assistant-agent-${stamp}`,
        requestId: `agent-${stamp}`,
        role: 'assistant',
        message: '',
        content: '',
        activities: [],
        isStreaming: true,
        createdAt: new Date().toISOString()
      });
      state.byChatId[chatId] = sortChatThreadItems(state.byChatId[chatId]);
    },
    appendAgentActivity: (state, action) => {
      const { chatId, type, payload = {}, seq, createdAt } = action.payload;
      if (!chatId || !type) {
        return;
      }
      ensureChatMessages(state, chatId);
      let assistant = findAgentStreamingAssistant(state, chatId);
      if (!assistant) {
        const stamp = Date.now();
        assistant = {
          id: `assistant-agent-${stamp}`,
          requestId: `agent-${stamp}`,
          role: 'assistant',
          message: '',
          content: '',
          activities: [],
          isStreaming: true,
          createdAt: new Date().toISOString()
        };
        state.byChatId[chatId].push(assistant);
        state.byChatId[chatId] = sortChatThreadItems(state.byChatId[chatId]);
        assistant = findAgentStreamingAssistant(state, chatId);
      }
      if (!assistant) {
        return;
      }
      if (!Array.isArray(assistant.activities)) {
        assistant.activities = [];
      }
      const id = `agent-activity-${seq ?? Date.now()}-${type}`;
      if (assistant.activities.some((item) => item.id === id)) {
        return;
      }
      assistant.activities.push({
        id,
        type,
        payload,
        seq: seq || 0,
        createdAt: createdAt || new Date().toISOString()
      });
    },
    finalizeAgentAssistant: (state, action) => {
      const { chatId, reply } = action.payload;
      const streamingMessage = findAgentStreamingAssistant(state, chatId);
      if (!streamingMessage) {
        return;
      }
      if (reply) {
        streamingMessage.message = reply;
        streamingMessage.content = reply;
      }
      streamingMessage.isStreaming = false;
      // Keep activities for collapsible "work details" in the bubble.
    },
    ensureAgentStreamingPlaceholder: (state, action) => {
      const chatId = action.payload;
      if (!chatId) {
        return;
      }
      ensureChatMessages(state, chatId);
      if (findAgentStreamingAssistant(state, chatId)) {
        return;
      }
      const stamp = Date.now();
      state.byChatId[chatId].push({
        id: `assistant-agent-${stamp}`,
        requestId: `agent-${stamp}`,
        role: 'assistant',
        message: '',
        content: '',
        activities: [],
        isStreaming: true,
        createdAt: new Date().toISOString()
      });
      state.byChatId[chatId] = sortChatThreadItems(state.byChatId[chatId]);
    },
    clearAgentTurnPlaceholders: (state, action) => {
      const chatId = action.payload;
      if (!chatId || !state.byChatId[chatId]) {
        return;
      }
      state.byChatId[chatId] = state.byChatId[chatId].filter(
        (item) =>
          !(item.role === 'assistant' && item.isStreaming && String(item.requestId || '').startsWith('agent-'))
      );
    },
    clearAgentActivity: (state, action) => {
      const chatId = action.payload;
      if (!chatId || !state.byChatId[chatId]) {
        return;
      }
      // Strip legacy standalone activity rows; keep activities on finalized assistant messages.
      state.byChatId[chatId] = state.byChatId[chatId]
        .filter((item) => item.role !== 'agent-activity')
        .map((item) => {
          if (
            item.role === 'assistant' &&
            item.isStreaming &&
            String(item.requestId || '').startsWith('agent-')
          ) {
            return { ...item, activities: [] };
          }
          return item;
        });
    },
    rollbackAgentTurnOptimistic: (state, action) => {
      const chatId = action.payload;
      if (!chatId || !state.byChatId[chatId]) {
        return;
      }
      state.byChatId[chatId] = state.byChatId[chatId].filter((item) => {
        const id = String(item.id || '');
        return (
          item.role !== 'agent-activity' &&
          !id.startsWith('user-agent-') &&
          !id.startsWith('assistant-agent-')
        );
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatMessages.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchChatMessages.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.historyError = null;
        const { chatId, messages } = action.payload;
        const previous = state.byChatId[chatId] || [];
        const previousActivitiesByText = new Map();
        previous.forEach((item) => {
          if (item.role !== 'assistant' || !Array.isArray(item.activities) || !item.activities.length) {
            return;
          }
          const text = (item.content || item.message || '').trim();
          if (text) {
            previousActivitiesByText.set(text, item.activities);
          } else if (String(item.requestId || '').startsWith('agent-')) {
            previousActivitiesByText.set('__agent_pending__', item.activities);
          }
        });

        const hasMatchingApiUserMessage = (candidate) =>
          messages.some((apiMessage) => {
            if (apiMessage.role !== 'user') {
              return false;
            }

            const sameText = (apiMessage.content || '').trim() === (candidate.content || '').trim();
            const candidateUrls = (candidate.imageUrls || []).join('|');
            const apiUrls = (apiMessage.imageUrls || []).join('|');

            return sameText && candidateUrls === apiUrls;
          });
        const pendingMessages = previous.filter((item) => {
          if (item.role === 'agent-activity') {
            return false;
          }

          // Keep locally-created user messages until API echoes them back.
          if (item.role === 'user' && String(item.id || '').startsWith('user-')) {
            return !hasMatchingApiUserMessage(item);
          }

          // Keep in-flight assistant only if API has not returned a matching reply yet.
          if (item.role === 'assistant' && item.isStreaming) {
            const text = (item.content || item.message || '').trim();
            if (!text) {
              return true;
            }
            const echoed = messages.some(
              (apiMessage) =>
                apiMessage.role === 'assistant' &&
                (apiMessage.message || apiMessage.content || '').trim() === text
            );
            return !echoed;
          }

          return false;
        });

        const mergedApiMessages = messages.map((apiMessage, index) => {
          if (apiMessage.role !== 'assistant') {
            return apiMessage;
          }
          const text = (apiMessage.message || apiMessage.content || '').trim();
          const matchedByText = text ? previousActivitiesByText.get(text) : null;
          const isLastAssistant =
            index ===
            messages.reduce((lastIndex, msg, msgIndex) => (msg.role === 'assistant' ? msgIndex : lastIndex), -1);
          const pending =
            !matchedByText && isLastAssistant
              ? previousActivitiesByText.get('__agent_pending__')
              : null;
          const activities = matchedByText || pending || apiMessage.activities;
          if (!activities?.length) {
            return apiMessage;
          }
          if (pending) {
            previousActivitiesByText.delete('__agent_pending__');
          }
          return { ...apiMessage, activities };
        });

        state.byChatId[chatId] = sortChatThreadItems([...mergedApiMessages, ...pendingMessages]);
      })
      .addCase(fetchChatMessages.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.error.message || 'Chat messages request failed.';
      })
      .addCase(sendMessage.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const { chatId, message, imageUrls = [], quotedText } = action.meta.arg;
        ensureChatMessages(state, chatId);
        state.generationByChatId[chatId] = { phase: 'connecting', sandbox: false };
        state.byChatId[chatId].push({
          id: `user-${Date.now()}`,
          role: 'user',
          content: message,
          imageUrls: Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [],
          quotedText: quotedText || null,
          createdAt: new Date().toISOString()
        });
        state.byChatId[chatId].push({
          id: `assistant-${action.meta.requestId}`,
          requestId: action.meta.requestId,
          role: 'assistant',
          message: '',
          content: '',
          isStreaming: true,
          createdAt: new Date().toISOString()
        });
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { chatId, response } = action.payload;
        const streamingMessage = findStreamingMessage(state, chatId, action.meta.requestId);

        if (streamingMessage) {
          finalizeStreamingMessage(state, {
            chatId,
            requestId: action.meta.requestId,
            reply: response.reply
          });
          return;
        }

        const chatMessages = state.byChatId[chatId] || [];
        const lastMessage = chatMessages[chatMessages.length - 1];

        if (lastMessage?.role === 'assistant') {
          return;
        }

        ensureChatMessages(state, chatId);
        state.byChatId[chatId].push({
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          message: response.reply,
          content: response.reply,
          createdAt: new Date().toISOString()
        });
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        const { chatId } = action.meta.arg;
        delete state.generationByChatId[chatId];
        const streamingMessage = findStreamingMessage(state, chatId, action.meta.requestId);

        if (action.meta.aborted) {
          state.error = null;

          if (streamingMessage) {
            if (streamingMessage.content) {
              streamingMessage.isStreaming = false;
            } else {
              state.byChatId[chatId] = state.byChatId[chatId].filter(
                (item) => item.requestId !== action.meta.requestId
              );
            }
          }

          return;
        }

        if (action.payload?.statusCode === 402) {
          state.error = null;

          if (streamingMessage) {
            if (streamingMessage.content) {
              streamingMessage.isStreaming = false;
            } else {
              state.byChatId[chatId] = state.byChatId[chatId].filter(
                (item) => item.requestId !== action.meta.requestId
              );
            }
          }

          return;
        }

        state.error = action.error.message || 'AI request failed.';

        if (streamingMessage) {
          if (streamingMessage.content) {
            streamingMessage.isStreaming = false;
          } else {
            streamingMessage.message = LLM_FALLBACK_ERROR_MESSAGE;
            streamingMessage.content = LLM_FALLBACK_ERROR_MESSAGE;
            streamingMessage.isStreaming = false;
          }
        }
      })
      .addCase(deleteProjectChat.fulfilled, (state, action) => {
        const { chatId } = action.payload;
        delete state.byChatId[chatId];
        delete state.latestResultByChatId[chatId];
        delete state.generationByChatId[chatId];
      });
  }
});

export const {
  hydrateProjectChat,
  setMessageFeedback,
  streamDeltaReceived,
  streamResultReceived,
  finalizeStreamingMessageAction,
  setGenerationPhase,
  clearGenerationPhase,
  appendAgentTurnOptimistic,
  appendAgentActivity,
  finalizeAgentAssistant,
  ensureAgentStreamingPlaceholder,
  clearAgentTurnPlaceholders,
  clearAgentActivity,
  rollbackAgentTurnOptimistic
} = chatSlice.actions;
export default chatSlice.reducer;
